// hooks/useReferrals.ts
// Referrals - currently demo mode only
// TODO: Add API endpoints for referrals

import { useState, useEffect, useCallback } from 'react';

interface ReferralCode {
  id: string;
  code: string;
  tenant_name: string;
  tenant_email: string;
  building_id: string;
  reward_amount: number;
  is_active: boolean;
  created_at: string;
}

interface Referral {
  id: string;
  referral_code_id: string;
  referee_name: string;
  referee_email: string;
  referee_phone: string | null;
  status: 'pending' | 'toured' | 'applied' | 'leased' | 'paid';
  notes: string | null;
  created_at: string;
}

type ReferralStatus = 'pending' | 'toured' | 'applied' | 'leased' | 'paid';

interface UseReferralCodeResult {
  referralCode: ReferralCode | null;
  loading: boolean;
  error: Error | null;
}

interface UseReferralsOptions {
  buildingId: string;
}

interface UseReferralsResult {
  codes: ReferralCode[];
  referrals: Referral[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface CreateReferralResult {
  success: boolean;
  error: Error | null;
  referralId: string | null;
}

// Demo referral code
const DEMO_REFERRAL_CODE: ReferralCode = {
  id: 'ref-1',
  code: 'SARAH2024',
  tenant_name: 'Sarah M.',
  tenant_email: 'sarah.m@example.com',
  building_id: 'bld-66-broadway',
  reward_amount: 250,
  is_active: true,
  created_at: new Date().toISOString()
};

// Hook to validate a referral code (public)
export function useReferralCode(code: string): UseReferralCodeResult {
  const [referralCode, setReferralCode] = useState<ReferralCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCode = async () => {
      setLoading(true);
      setError(null);

      await new Promise(resolve => setTimeout(resolve, 200));

      if (code.toUpperCase() === 'SARAH2024') {
        setReferralCode(DEMO_REFERRAL_CODE);
      } else if (code) {
        setError(new Error('Invalid referral code'));
      }

      setLoading(false);
    };

    if (code) {
      fetchCode();
    } else {
      setLoading(false);
    }
  }, [code]);

  return { referralCode, loading, error };
}

// Hook for admin to view referral codes and referrals
export function useReferrals({ buildingId }: UseReferralsOptions): UseReferralsResult {
  const [codes, setCodes] = useState<ReferralCode[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Demo mode
    setCodes([DEMO_REFERRAL_CODE]);
    setReferrals([]);
    setLoading(false);
  }, [buildingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { codes, referrals, loading, error, refetch: fetchData };
}

// Function to create a referral (public - when someone uses a referral code)
export async function createReferral(
  codeId: string,
  data: {
    name: string;
    email: string;
    phone?: string;
  }
): Promise<CreateReferralResult> {
  console.log('[Demo] Creating referral:', { codeId, ...data });
  return {
    success: true,
    error: null,
    referralId: `demo-${Date.now()}`
  };
}

// Function to update referral status (admin)
export async function updateReferralStatus(
  referralId: string,
  status: ReferralStatus,
  notes?: string
): Promise<{ success: boolean; error: Error | null }> {
  console.log('[Demo] Updating referral status:', { referralId, status, notes });
  return { success: true, error: null };
}
